import { useRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { useChatComposer, useChatIsStreaming, useChatIsInputDisabled, useChatOnDisabledClick } from "../ChatContext";

/**
 * Default accept attribute for file inputs.
 * Images + PDFs — override via the `accept` prop.
 */
const DEFAULT_FILE_ACCEPT = "image/png,image/jpeg,image/gif,image/webp,application/pdf";

export interface FileButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "onClick"
> {
  children?: ReactNode;
  onFilesSelected?: (files: FileList) => void;
  accept?: string;
  multiple?: boolean;
}

export function FileButton({
  children,
  onFilesSelected,
  accept = DEFAULT_FILE_ACCEPT,
  multiple = true,
  disabled,
  className,
  ...props
}: FileButtonProps) {
  const composer = useChatComposer();
  const isStreaming = useChatIsStreaming();
  const chatDisabled = useChatIsInputDisabled();
  const onDisabledClick = useChatOnDisabledClick();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (chatDisabled && onDisabledClick) {
      onDisabledClick();
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (onFilesSelected) {
        onFilesSelected(files);
      } else {
        composer.addFiles(files);
      }
    }
    e.target.value = "";
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={chatDisabled || (disabled ?? isStreaming)}
        className={twMerge(
          "p-0",
          "hover:opacity-70 transition-opacity",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          className
        )}
        {...props}
      >
        {children}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />
    </>
  );
}
